import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'

export const profileQueryKey = (userId: string | null) => ['profile', userId] as const

function invalidateProfileQueries(client: QueryClient) {
  client.invalidateQueries({ queryKey: ['profile'] })
}

function invalidateFeedQueries(client: QueryClient) {
  client.invalidateQueries({ queryKey: ['feed'] })
}

export interface ProfileData {
  name: string
  birthDate: string
  gender: 'MALE' | 'FEMALE'
  cityId: number
  city: string
  bio?: string
  goals: string[]
  heightCm?: number
  education?: string
  occupation?: string
  smoking?: 'NO' | 'SOMETIMES' | 'YES'
  alcohol?: 'NO' | 'SOMETIMES' | 'YES'
  kids?: 'HAVE' | 'WANT' | 'NO' | 'UNDECIDED'
  searchGender: 'MALE' | 'FEMALE' | 'ANY'
  searchAgeFrom: number
  searchAgeTo: number
  searchRadius: number
}

export function useProfile(userId: string | null) {
  return useQuery({
    queryKey: profileQueryKey(userId),
    queryFn: async () => {
      if (!userId) return null
      try {
        return await apiClient.get<any>('/api/profile/me')
      } catch (err) {
        const statusCode = (err as { statusCode?: number })?.statusCode
        if (statusCode === 404) {
          return null
        }
        throw err
      }
    },
    enabled: !!userId,
    staleTime: 30_000,
    refetchOnMount: 'always',
  })
}

export function useCreateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ProfileData) =>
      apiClient.post('/api/profile', data),
    onSuccess: () => {
      invalidateProfileQueries(qc)
      invalidateFeedQueries(qc)
    },
  })
}

export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<ProfileData>) =>
      apiClient.put('/api/profile/me', data),
    onSuccess: () => {
      invalidateProfileQueries(qc)
      invalidateFeedQueries(qc)
    },
  })
}

export function useSetInterests() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (interestIds: number[]) =>
      apiClient.post('/api/profile/interests', { interestIds }),
    onSuccess: () => {
      invalidateProfileQueries(qc)
      invalidateFeedQueries(qc)
    },
  })
}

/** Номер из MAX `requestContact`, сохраняется на сервере с флагом верификации. */
export function useVerifyMaxPhone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (phone: string) =>
      apiClient.post<unknown>('/api/profile/verify-max-phone', { phone }),
    onSuccess: () => {
      invalidateProfileQueries(qc)
    },
  })
}
